import { Button, HStack, Select } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage } from 'react-intl';

import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { TriggerEndDate } from '@/App/tiles/Option/mainFields/TriggerEndDate.tsx';
import { TriggerStartDate } from '@/App/tiles/Option/mainFields/TriggerStartDate.tsx';
import { TriggerType } from '@/App/tiles/Option/mainFields/TriggerType.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput/index.tsx';
import type { OptionBarrier, PossibleProperty } from '@/store/option/state.ts';

type TriggerTypeProps = {
  tileId: string;
  barrier?: OptionBarrier;
  numberOfBarrier?: number;
  possibleNatures?: PossibleProperty<OptionBarrier['nature']>;
  possibleTypes?: PossibleProperty<OptionBarrier['type']>;
};
type CollapseStatus = 'collapsed' | 'expanded';

export const Triggers = ({
  tileId,
  expanded,
  onClick,
}: {
  tileId: string;
  expanded: boolean;
  onClick: (expanded: CollapseStatus) => void;
}) => {
  const status = expanded ? 'expanded' : 'collapsed';

  const { barrier1, barrier2, possibleNatures1, possibleTypes1, possibleNatures2, possibleTypes2 } = useOptionSelector(
    (option) => {
      const leg = option.properties.legs[0];
      return {
        barrier1: leg?.barrier1,
        barrier2: leg?.barrier2,
        possibleNatures1: leg?.barrier1?.possibleNatures,
        possibleTypes1: leg?.barrier1?.possibleTypes,
        possibleNatures2: leg?.barrier2?.possibleNatures,
        possibleTypes2: leg?.barrier2?.possibleTypes,
      };
    },
  );
  const handleCollapseTriggers = () => {
    const newStatus = status === 'collapsed' ? 'expanded' : 'collapsed';
    onClick(newStatus);
  };

  return (
    <>
      {barrier1 && (
        <HStack verticalAlignment="center" fullWidth className="px-2">
          <div className="flex-fill border-top" style={{ height: 1 }} />

          <Button size="sm" purpose="flat" onClick={handleCollapseTriggers} data-e2e="triggers-toggle-button">
            <FormattedMessage id="option.sections.triggers" />
            <i className="icon icon-sm">{status === 'collapsed' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</i>
          </Button>

          <div className="flex-fill border-top" style={{ height: 1 }} />
        </HStack>
      )}

      {status === 'expanded' && (
        <>
          <TriggerType tileId={tileId} />

          {barrier1 && (
            <>
              <Trigger
                tileId={tileId}
                barrier={barrier1}
                numberOfBarrier={1}
                possibleNatures={possibleNatures1}
                possibleTypes={possibleTypes1}
              />
              {barrier1.nature === 'WINDOW' && (
                <>
                  <TriggerStartDate tileId={tileId} barrier={barrier1} numberOfBarrier={1} />
                  <TriggerEndDate tileId={tileId} barrier={barrier1} numberOfBarrier={1} />
                </>
              )}
            </>
          )}
          {barrier2 && (
            <>
              <Trigger
                tileId={tileId}
                barrier={barrier2}
                numberOfBarrier={2}
                possibleNatures={possibleNatures2}
                possibleTypes={possibleTypes2}
              />
              {barrier2.nature === 'WINDOW' && (
                <>
                  <TriggerStartDate tileId={tileId} barrier={barrier2} numberOfBarrier={2} />
                  <TriggerEndDate tileId={tileId} barrier={barrier2} numberOfBarrier={2} />
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export function Trigger({
  tileId,
  barrier,
  numberOfBarrier = 1,
  possibleNatures,
  possibleTypes,
}: Readonly<TriggerTypeProps>) {
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const [checkOption] = useCheckOption();
  function handleBarrierLevelChange(level: number | undefined) {
    sgwtWebAnalytics?.trackEvent('Option', 'trigger level changed', String(level));

    checkOption({
      tileId,
      legProperties: {
        legId: `${tileId}/0`,
        [`barrier${numberOfBarrier}`]: { level: level },
      },
    });
  }

  function handleBarrierTypeChange(triggerType: OptionBarrier['type']) {
    sgwtWebAnalytics?.trackEvent('Option', 'trigger type changed', String(triggerType));

    checkOption({
      tileId,
      legProperties: {
        legId: `${tileId}/0`,
        [`barrier${numberOfBarrier}`]: { type: triggerType },
      },
    });
  }

  function handleBarrierNatureChange(triggerNature: OptionBarrier['nature']) {
    sgwtWebAnalytics?.trackEvent('Option', 'trigger nature changed', String(triggerNature));

    checkOption({
      tileId,
      legProperties: {
        legId: `${tileId}/0`,
        [`barrier${numberOfBarrier}`]: { nature: triggerNature },
      },
    });
  }

  return (
    <>
      <OptionFormGroup label={<FormattedMessage id="option.fields.trigger" values={{ number: numberOfBarrier }} />}>
        <SgNumberInput
          valuePosition="start"
          selectAllOnFocus
          value={barrier?.level ?? undefined}
          allowNegative={false}
          maxDecimals={9}
          onChange={handleBarrierLevelChange}
          size="sm"
          data-e2e="leverage-ratio"
        />
      </OptionFormGroup>
      <OptionFormGroup label="">
        <div className="d-flex flex-grow-1 gap-1">
          <Select
            data-e2e="trigger-types-select"
            className="form-select form-select-sm"
            selectedItemValue={barrier?.type ?? possibleTypes?.default}
            itemsAsObjects={possibleTypes?.possible ?? []}
            onChange={handleBarrierTypeChange}
          />
          <Select
            data-e2e="trigger-nature-select"
            className="form-select form-select-sm"
            selectedItemValue={barrier?.nature ?? possibleNatures?.default}
            itemsAsObjects={possibleNatures?.possible ?? []}
            onChange={handleBarrierNatureChange}
          />
        </div>
      </OptionFormGroup>
    </>
  );
}
